import {
  cloneElement,
  forwardRef,
  isValidElement,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import { useEventCallback, useForkRef } from '@spazious/react-hooks';

import { createChainedFunction, ownerDocument } from '../../utils/index.js';
import { FocusTrap } from '../FocusTrap/index.js';
import { Portal, PortalProps } from '../Portal/index.js';

import { ariaHidden, ModalManager, ModalMountRef } from './ModalManager.js';

export type ModalOwnerState = ModalBaseProps & {
  closeAfterTransition: boolean;
  disableAutoFocus: boolean;
  disableEnforceFocus: boolean;
  disableEscapeKeyDown: boolean;
  disablePortal: boolean;
  disableRestoreFocus: boolean;
  disableScrollLock: boolean;
  exited: boolean;
  hideBackdrop: boolean;
  keepMounted: boolean;
};

export type ModalBaseProps<D extends React.ElementType = 'div'> = {
  component?: D;
  focusVisible?: boolean;
  /**
   * A single child content element.
   */
  children: React.ReactElement;
  /**
   * When set to true the Modal waits until a nested Transition is completed before closing.
   * @default false
   */
  closeAfterTransition?: boolean;
  /**
   * An HTML element or function that returns one.
   * The `container` will have the portal children appended to it.
   *
   * By default, it uses the body of the top-level document object,
   * so it's simply `document.body` most of the time.
   */
  container?: PortalProps['container'];
  /**
   * If `true`, the modal will not automatically shift focus to itself when it opens, and
   * replace it to the last focused element when it closes.
   * This also works correctly with any modal children that have the `disableAutoFocus` prop.
   *
   * Generally this should never be set to `true` as it makes the modal less
   * accessible to assistive technologies, like screen readers.
   * @default false
   */
  disableAutoFocus?: boolean;
  /**
   * If `true`, the modal will not prevent focus from leaving the modal while open.
   *
   * Generally this should never be set to `true` as it makes the modal less
   * accessible to assistive technologies, like screen readers.
   * @default false
   */
  disableEnforceFocus?: boolean;
  /**
   * If `true`, hitting escape will not fire the `onClose` callback.
   * @default false
   */
  disableEscapeKeyDown?: boolean;
  /**
   * The `children` will be under the DOM hierarchy of the parent component.
   * @default false
   */
  disablePortal?: PortalProps['disablePortal'];
  /**
   * If `true`, the modal will not restore focus to previously focused element once
   * modal is hidden or unmounted.
   * @default false
   */
  disableRestoreFocus?: boolean;
  /**
   * Disable the scroll lock behavior.
   * @default false
   */
  disableScrollLock?: boolean;
  /**
   * If `true`, the backdrop is not rendered.
   * @default false
   */
  hideBackdrop?: boolean;
  /**
   * Always keep the children in the DOM.
   * This prop can be useful in SEO situation or
   * when you want to maximize the responsiveness of the Modal.
   * @default false
   */
  keepMounted?: boolean;
  /**
   * Callback fired when the component requests to be closed.
   * The `reason` parameter can optionally be used to control the response to `onClose`.
   *
   * @param {object} event The event source of the callback.
   * @param {string} reason Can be: `"escapeKeyDown"`, `"backdropClick"`.
   */
  onClose?: {
    bivarianceHack(event: {}, reason: 'backdropClick' | 'escapeKeyDown'): void;
  }['bivarianceHack'];
  /**
   * If `true`, the component is shown.
   */
  open: boolean;

  'aria-hidden'?: boolean;
  manager?: ModalManager;
  onKeyDown?: React.KeyboardEventHandler;
  onTransitionEnter?: () => void;
  onTransitionExited?: () => void;
  backdropComponent?: React.ElementType;
};

function getContainer(container: ModalBaseProps['container']) {
  return typeof container === 'function' ? container() : container;
}

function getHasTransition(children: ModalBaseProps['children']) {
  return children ? children.props && children.props.hasOwnProperty('in') : false;
}

// A modal manager used to track and manage the state of open Modals.
// Modals don't open on the server so this won't conflict with concurrent requests.
const defaultManager = new ModalManager();

/**
 * Modal is a lower-level construct that is leveraged by the following components:
 */
export const ModalBase = forwardRef(
  <T extends React.ElementType>(
    props: ModalBaseProps<T>,
    forwardedRef: React.ForwardedRef<React.ElementRef<T>> | undefined
  ) => {
    const {
      children,
      closeAfterTransition = false,
      component: Component = 'div' as T,
      container,
      disableAutoFocus = false,
      disableEnforceFocus = false,
      disableEscapeKeyDown = false,
      disablePortal = false,
      disableRestoreFocus = false,
      disableScrollLock = false,
      hideBackdrop = false,
      keepMounted = false,
      onClose,
      open,
      // private
      manager = defaultManager,
      onKeyDown,
      onTransitionEnter,
      onTransitionExited,
      backdropComponent: BackdropComponent = 'div',
      // ...restProps
    } = props;

    const [exited, setExited] = useState(!open);
    const modal = useRef<{
      modalRef?: HTMLElement | null;
      mountNode?: Node | null;
    }>({});
    const mountNodeRef = useRef<Node | null>(null);
    const modalRef = useRef<HTMLElement>(null);
    const handleRef = useForkRef(modalRef, forwardedRef as React.Ref<HTMLElement>);
    const hasTransition = getHasTransition(children);

    const ariaHiddenProp = props['aria-hidden'] ?? true;

    const getDoc = () => ownerDocument(mountNodeRef.current);
    const getModal = () => {
      // if (!modalRef.current || !mountNodeRef.current) {
      //   throw new Error(
      //     'ModalBase: Expected modal components to be rendered into the same ' +
      //       'component tree. Ensure that your modal components are rendered ' +
      //       'inside ModalBase.'
      //   );
      // }

      modal.current.modalRef = modalRef.current;
      modal.current.mountNode = mountNodeRef.current;

      return modal.current as ModalMountRef;
    };

    const handleMounted = () => {
      manager.mount(getModal(), { disableScrollLock });

      // Fix a bug on Chrome where the scroll isn't initially 0.
      if (modalRef.current) {
        modalRef.current.scrollTop = 0;
      }
    };

    const handleOpen = useEventCallback(() => {
      const resolvedContainer = getContainer(container) || getDoc().body;

      manager.add(getModal(), resolvedContainer as HTMLElement);

      // The element was already mounted.
      if (modalRef.current) {
        handleMounted();
      }
    });

    const isTopModal = useCallback(() => manager.isTopModal(getModal()), [manager]);

    const handlePortalRef = useEventCallback((node: Element) => {
      mountNodeRef.current = node;

      if (!node || !modalRef.current) {
        return;
      }

      if (open && isTopModal()) {
        handleMounted();
      } else {
        ariaHidden(modalRef.current, ariaHiddenProp);
      }
    });

    const handleClose = useCallback(() => {
      manager.remove(getModal(), ariaHiddenProp);
    }, [manager, ariaHiddenProp]);

    useEffect(() => {
      return () => {
        handleClose();
      };
    }, [handleClose]);

    useEffect(() => {
      if (open) {
        handleOpen();
      } else if (!hasTransition || !closeAfterTransition) {
        handleClose();
      }
    }, [open, handleClose, hasTransition, closeAfterTransition, handleOpen]);

    // const ownerState: ModalOwnerState = {
    //   ...props,
    //   closeAfterTransition,
    //   disableAutoFocus,
    //   disableEnforceFocus,
    //   disableEscapeKeyDown,
    //   disablePortal,
    //   disableRestoreFocus,
    //   disableScrollLock,
    //   exited,
    //   hideBackdrop,
    //   keepMounted,
    // };

    const handleEnter = () => {
      setExited(false);

      if (onTransitionEnter) {
        onTransitionEnter();
      }
    };

    const handleExited = () => {
      setExited(true);

      if (onTransitionExited) {
        onTransitionExited();
      }

      if (closeAfterTransition) {
        handleClose();
      }
    };

    const handleBackdropClick = (event: React.MouseEvent<HTMLElement>) => {
      if (event.target !== event.currentTarget) {
        return;
      }

      if (onClose) {
        onClose(event, 'backdropClick');
      }
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
      if (onKeyDown) {
        onKeyDown(event);
      }

      // The handler doesn't take event.defaultPrevented into account:
      //
      // event.preventDefault() is meant to stop default behaviors like
      // clicking a checkbox to check it, hitting a button to submit a form,
      // and hitting left arrow to move the cursor in a text input etc.
      // Only special HTML elements have these default behaviors.
      if (event.key !== 'Escape' || !isTopModal()) {
        return;
      }

      if (!disableEscapeKeyDown) {
        // Swallow the event, in case someone is listening for the escape key on the body.
        event.stopPropagation();

        if (onClose) {
          onClose(event, 'escapeKeyDown');
        }
      }
    };

    const childProps: {
      onEnter?: () => void;
      onExited?: () => void;
      tabIndex?: string;
    } = {};

    if (children.props && children.props.tabIndex === undefined) {
      childProps.tabIndex = '-1';
    }

    // It's a Transition like component
    if (children.props && typeof children.props.onEnter === 'function') {
      childProps.onEnter = createChainedFunction(handleEnter, children.props.onEnter);
    }

    if (children.props && typeof children.props.onExited === 'function') {
      childProps.onExited = createChainedFunction(handleExited, children.props.onExited);
    }

    // const rootProps = {
    //   elementType: Root,
    //   externalForwardedProps: other,
    //   additionalProps: {
    //     ref: handleRef,
    //     role: 'presentation',
    //     onKeyDown: handleKeyDown,
    //   },
    //   ownerState,
    // };

    if (!keepMounted && !open && (!hasTransition || exited)) {
      return null;
    }

    return (
      <Portal ref={handlePortalRef} container={container} disablePortal={disablePortal}>
        {/*
         * Marking an element with the role presentation indicates to assistive technology
         * that this element should be ignored; it exists to support the web application and
         * is not meant for humans to interact with directly.
         * https://github.com/evcohen/eslint-plugin-jsx-a11y/blob/master/docs/rules/no-static-element-interactions.md
         */}
        {/* @ts-ignore */}
        <Component ref={handleRef} role="presentation" onKeyDown={handleKeyDown}>
          {!hideBackdrop && BackdropComponent ? (
            <BackdropComponent aria-hidden onClick={handleBackdropClick} />
          ) : null}
          {isValidElement(children) ? (
            <FocusTrap
              disableAutoFocus={disableAutoFocus}
              disableEnforceFocus={disableEnforceFocus}
              disableRestoreFocus={disableRestoreFocus}
              isEnabled={isTopModal}
              open={open}
            >
              {cloneElement(children, childProps)}
            </FocusTrap>
          ) : (
            children
          )}
        </Component>
      </Portal>
    );
  }
);
